import { ApolloCache, FetchResult, MutationHookOptions, MutationResult, useMutation } from '@apollo/client';
import { ApplyWeightedRdPercentageToExpensesErrorSchema, createError, getExtendedErrors } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import { ToastDurationOptions } from '@ui-components/neo.tax';
import { useCallback } from 'react';

import {
  evictCache,
  ToastConfigState,
  UpdateRdExpenseProjectAllocationsMutation,
  ApplyWeightedRdPercentageToExpensesMutation,
  APPLY_WEIGHTED_RD_PERCENTAGE_TO_EXPENSES,
  ApplyWeightedRdPercentageToExpensesMutationVariables,
} from '@neo.tax/shared';

type ApplyWeightedRdPercentageToExpensesParams =
  | {
      rdExpenseIds: Array<string>;
      desiredRdPercentage: number;
      applyToAll?: false;
    }
  | {
      desiredRdPercentage: number;
      applyToAll: true;
      filter?: Record<string, unknown>;
    };

type UseWeightedRdPercentageToExpenses = {
  applyWeightedRdPercentageToExpenses: (
    params: ApplyWeightedRdPercentageToExpensesParams,
  ) => Promise<FetchResult<ApplyWeightedRdPercentageToExpensesMutation>>;
} & MutationResult<ApplyWeightedRdPercentageToExpensesMutation>;

const APPLY_RD_PERCENTAGE_TO_EXPENSES_REQUEST_KEY = 'APPLY_RD_PERCENTAGE_TO_EXPENSES_REQUEST_KEY';

export const useWeightedRdPercentageToExpenses = ({
  fiscalYearId,
  onSuccess,
  options,
}: {
  onSuccess: () => void;
  fiscalYearId: string;
  options?: MutationHookOptions<
    ApplyWeightedRdPercentageToExpensesMutation,
    ApplyWeightedRdPercentageToExpensesMutationVariables
  >;
}): UseWeightedRdPercentageToExpenses => {
  const [applyWeightedRdPercentageToExpensesMutation, applyWeightedRdPercentageToExpensesResult] = useMutation<
    ApplyWeightedRdPercentageToExpensesMutation,
    ApplyWeightedRdPercentageToExpensesMutationVariables
  >(APPLY_WEIGHTED_RD_PERCENTAGE_TO_EXPENSES, {
    onError: (error) => {
      try {
        const extendedErrors = getExtendedErrors(error, ApplyWeightedRdPercentageToExpensesErrorSchema);
        const errorMessage = extendedErrors.map((error) => error.description).join(', ');
        ToastConfigState.pushErrorToast(errorMessage, {
          duration: ToastDurationOptions.LONG,
        });
      } catch {
        Sentry.addBreadcrumb({ level: 'error', data: { error } });
        Sentry.captureException(
          createError('ApplyWeightedRdPercentageToExpensesError', 'Failed to set R&D percentage for expenses.'),
        );
        ToastConfigState.pushErrorToast('Failed to set R&D percentage for expenses. Please try again.', {
          duration: ToastDurationOptions.LONG,
        });
      }
    },
    context: {
      serializationKey: APPLY_RD_PERCENTAGE_TO_EXPENSES_REQUEST_KEY,
      debounceKey: APPLY_RD_PERCENTAGE_TO_EXPENSES_REQUEST_KEY,
      successToast: {
        title: 'Successfully set RD percentage for expenses',
      },
    },
    ...options,
  });

  const applyWeightedRdPercentageToExpenses = useCallback(
    (
      params: ApplyWeightedRdPercentageToExpensesParams,
    ): Promise<FetchResult<ApplyWeightedRdPercentageToExpensesMutation>> =>
      applyWeightedRdPercentageToExpensesMutation({
        variables: {
          fiscalYearId,
          applyWeightedRdPercentageToExpensesInput: params.applyToAll
            ? { fiscalYearId, desiredRdPercentage: params.desiredRdPercentage, applyToAll: true }
            : { fiscalYearId, rdExpenseIds: params.rdExpenseIds, desiredRdPercentage: params.desiredRdPercentage },
          ...(params.applyToAll && params.filter ? { filter: params.filter } : {}),
        },
        onCompleted: onSuccess,
        update: (cache: ApolloCache<UpdateRdExpenseProjectAllocationsMutation>): void => {
          evictCache(cache, [
            'getPayrollQres',
            'getPayrollQreSummary',
            'getAccountingQres',
            'getAccountingQreSummary',
            'getRdProjectTeamMembers',
            'getRdExpense',
            'getRdExpenseProjectAllocations',
          ]);
        },
      }),
    [applyWeightedRdPercentageToExpensesMutation, fiscalYearId, onSuccess],
  );
  return {
    applyWeightedRdPercentageToExpenses,
    ...applyWeightedRdPercentageToExpensesResult,
  };
};
